import React, { useState } from 'react';
import { X, AlertTriangle, DollarSign, Mail, CheckCircle, XCircle } from 'lucide-react';
import { fairManagerApi } from '../services/api';
import { FairCancellationResponse } from '../types';
import { getErrorMessage } from '../utils/errorUtils';
import { formatCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities

interface FairCancellationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  fair: {
    id: number;
    name: string;
  };
}

export default function FairCancellationModal({
  isOpen,
  onClose,
  onSuccess,
  fair
}: FairCancellationModalProps) {
  const [cancellationReason, setCancellationReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<FairCancellationResponse | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation: cancellation reason required
    if (!cancellationReason.trim()) {
      setError('Cancellation reason is required');
      return;
    }

    // Validation: max 1000 characters
    if (cancellationReason.length > 1000) {
      setError('Cancellation reason must be 1000 characters or less');
      return;
    }

    try {
      setProcessing(true);
      const response = await fairManagerApi.cancelFairWithRefunds(fair.id, cancellationReason);
      setResult(response);

      // Don't auto-close - let user see results first
      // onSuccess will be called when they click "Done"
    } catch (err: unknown) {
      console.error('Failed to cancel fair:', err);
      setError(getErrorMessage(err, 'Failed to cancel fair and process refunds'));
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    if (result) {
      onSuccess(); // Refresh parent component data
    }
    setCancellationReason('');
    setError(null);
    setResult(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {result ? 'Cancellation Complete' : 'Cancel Fair'}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600"
            disabled={processing}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Success Results */}
        {result && (
          <div className="p-6 space-y-6">
            {/* Success Header */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-green-600 mt-0.5" />
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-green-900">
                    Fair Successfully Cancelled
                  </h3>
                  <p className="text-sm text-green-700 mt-1">
                    {result.fair_name} has been cancelled and all vendor refunds have been processed.
                  </p>
                </div>
              </div>
            </div>

            {/* Refund Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Total Reservations */}
              <div className="bg-moonbone border border-teal-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-ink">Total Reservations</p>
                    <p className="text-2xl font-bold text-secondary mt-1">
                      {result.total_reservations}
                    </p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-yellow-500" />
                </div>
              </div>

              {/* Refunds Processed */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-900">Refunds Processed</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      {result.refunds_processed}
                    </p>
                  </div>
                  <DollarSign className="h-8 w-8 text-green-400" />
                </div>
              </div>

              {/* Emails Sent */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-900">Emails Sent</p>
                    <p className="text-2xl font-bold text-purple-600 mt-1">
                      {result.emails_sent}
                    </p>
                  </div>
                  <Mail className="h-8 w-8 text-purple-400" />
                </div>
              </div>
            </div>

            {/* Failures (if any) */}
            {(result.refunds_failed > 0 || result.emails_failed > 0) && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertTriangle className="h-6 w-6 text-yellow-600 mt-0.5" />
                  <div className="ml-3">
                    <h4 className="text-sm font-semibold text-yellow-900">
                      Some Operations Failed
                    </h4>
                    <div className="text-sm text-yellow-700 mt-2 space-y-1">
                      {result.refunds_failed > 0 && (
                        <p>• {result.refunds_failed} refund(s) failed - please review and retry manually</p>
                      )}
                      {result.emails_failed > 0 && (
                        <p>• {result.emails_failed} email notification(s) failed - vendors may need to be contacted directly</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Refund Details Table */}
            {result.refund_details && result.refund_details.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Refund Details</h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Reservation
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Vendor
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Amount
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {result.refund_details.map((detail) => (
                          <tr key={detail.reservation_id}>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              #{detail.reservation_id}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {detail.vendor_email}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {formatCurrency(detail.amount)} {/* Issue #905: Decimal.js formatting */}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {detail.success ? (
                                <div className="flex items-center text-green-600">
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  <span>Success</span>
                                </div>
                              ) : (
                                <div className="flex items-center text-red-600">
                                  <XCircle className="h-4 w-4 mr-1" />
                                  <span title={detail.error}>Failed</span>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Done Button */}
            <div className="flex justify-end">
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary"
              >
                Done
              </button>
            </div>
          </div>
        )}

        {/* Confirmation Form */}
        {!result && (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Warning Alert */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertTriangle className="h-6 w-6 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="ml-3">
                  <h3 className="text-sm font-semibold text-yellow-900">
                    Warning: This Action Cannot Be Undone
                  </h3>
                  <div className="text-sm text-yellow-700 mt-2 space-y-1">
                    <p>Cancelling this fair will:</p>
                    <ul className="list-disc list-inside ml-2 mt-2 space-y-1">
                      <li>Cancel all vendor reservations for this fair</li>
                      <li>Process full refunds to all confirmed reservations via Stripe</li>
                      <li>Send cancellation emails to all affected vendors</li>
                      <li>Mark the fair as permanently cancelled</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Fair Information */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Fair Being Cancelled</h4>
              <p className="text-base font-medium text-gray-800">{fair.name}</p>
              <p className="text-sm text-gray-600 mt-1">Fair ID: #{fair.id}</p>
            </div>

            {/* Cancellation Reason */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Cancellation Reason *
                </label>
                <span className={`text-xs ${cancellationReason.length > 1000 ? 'text-red-600' : 'text-gray-500'}`}>
                  {cancellationReason.length}/1000
                </span>
              </div>
              <textarea
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Explain why this fair is being cancelled. This message will be sent to all vendors."
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                required
                maxLength={1000}
                disabled={processing}
              />
              <p className="text-xs text-gray-500 mt-1">
                This reason will be included in the cancellation email sent to all vendors.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={processing}
              >
                {processing ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </span>
                ) : (
                  'Confirm Cancellation'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
